import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AuthModal from '../components/auth/AuthModal';

const { FiUsers, FiTarget, FiTrendingUp, FiArrowRight, FiCode, FiDollarSign, FiBookOpen, FiBriefcase } = FiIcons;

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleGetStarted = () => {
    if (user) {
      navigate('/profile-wizard');
    } else {
      setShowAuthModal(true);
    }
  };

  const features = [
    {
      icon: FiTarget,
      title: 'Smart Matching',
      description: 'AI-powered algorithm matches you with the right industry professionals based on your needs and goals.'
    },
    {
      icon: FiUsers,
      title: 'Industry Network',
      description: 'Connect with developers, publishers, investors, and mentors all in one platform.'
    },
    {
      icon: FiTrendingUp,
      title: 'Growth Opportunities',
      description: 'Find partnerships, funding, mentorship, and publishing deals to grow your game business.'
    }
  ];

  const roles = [
    {
      icon: FiCode,
      title: 'Developers',
      description: 'Find publishers, investors, and mentors to bring your games to market.'
    },
    {
      icon: FiBriefcase,
      title: 'Publishers',
      description: 'Discover talented developers and promising game projects to publish.'
    },
    {
      icon: FiDollarSign,
      title: 'Investors',
      description: 'Connect with innovative game studios and high-potential projects.'
    },
    {
      icon: FiBookOpen,
      title: 'Mentors',
      description: 'Share your expertise and guide the next generation of game creators.'
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              {/* Logo in Hero */}
              <div className="mb-8">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759968337028-Matchmaker%20%281%29.png" 
                  alt="962.Games Business Matchmaker" 
                  className="h-20 w-auto mx-auto"
                />
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Connect. Create. <span className="text-indigo-600">Succeed.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                962.Games Business Matchmaker brings together developers, publishers, investors, and mentors in the video game industry. Find your perfect match and grow your game business.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetStarted}
                className="bg-indigo-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition-colors inline-flex items-center space-x-2"
              >
                <span>Create Your Profile</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>

          {/* Background decoration */}
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-12">
            <div className="w-72 h-72 bg-indigo-100 rounded-full opacity-50"></div>
          </div>
          <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12">
            <div className="w-96 h-96 bg-purple-100 rounded-full opacity-30"></div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose 962.Games Business Matchmaker?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our platform is designed specifically for the gaming industry, connecting the right people at the right time.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Roles Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Perfect for Everyone in Gaming
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Whether you're building games, publishing them, investing in them, or mentoring others, 962.Games Business Matchmaker has something for you.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {roles.map((role, index) => (
                <motion.div
                  key={role.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={role.icon} className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {role.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {role.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-indigo-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Find Your Perfect Match?
              </h2>
              <p className="text-xl text-indigo-100 mb-8">
                Join thousands of game industry professionals already using 962.Games Business Matchmaker to grow their businesses and careers.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetStarted}
                className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
              >
                <span>Get Started Today</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>
        </section>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
};

export default LandingPage;